import { useEffect, useMemo, useState } from "react";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { DataState } from "../components/ui/DataState";
import type { AdminUserRow, Organization, Role } from "../lib/types";

const ROLES: { value: Role; label: string }[] = [
  { value: "community_member", label: "Community member" },
  { value: "trusted_verifier", label: "Trusted verifier" },
  { value: "organization_admin", label: "Organization admin" },
  { value: "platform_admin", label: "Platform admin" },
];

export default function AdminUsers() {
  const api = useApi();
  const { user: currentUser } = useCurrentUser();
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | Role>("all");

  const load = async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([
        api.get<{ users: AdminUserRow[] }>("/api/admin/users"),
        api.get<{ organizations: Organization[] }>("/api/organizations"),
      ]);
      setUsers(u.users);
      setOrgs(o.organizations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const filtered = useMemo(() => {
    if (!users) return [];
    if (filter === "all") return users;
    return users.filter((u) => u.role === filter);
  }, [users, filter]);

  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">User management</h1>
          <p className="mt-2 max-w-3xl text-slate-300">
            Promote trusted community members, link organization admins to
            their orgs, or suspend an account. Role changes take effect on the
            user's next request.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-slate-400">
            Filter
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="rounded-lg border border-white/10 bg-ink-900/60 px-3 py-2 text-sm text-slate-100"
          >
            <option value="all">All roles</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={users}
          empty={!!users && filtered.length === 0}
          loadingMessage="Loading users…"
          emptyMessage="No users match this filter."
        >
          {() => (
            <div className="grid gap-4">
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  row={u}
                  orgs={orgs}
                  isSelf={currentUser?.id === u.id}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}

function UserRow({
  row,
  orgs,
  isSelf,
  onChanged,
}: {
  row: AdminUserRow;
  orgs: Organization[];
  isSelf: boolean;
  onChanged: () => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/admin/users/${row.id}`, patch);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={row.is_active ? "success" : "critical"}>
          {row.is_active ? "active" : "suspended"}
        </Badge>
        <Badge tone="neutral">{row.role.replace("_", " ")}</Badge>
        {row.organization_name && <Badge>{row.organization_name}</Badge>}
        {row.neighborhood && <Badge>{row.neighborhood}</Badge>}
        {isSelf && <Badge tone="warning">you</Badge>}
      </div>
      <div className="mt-3 grid gap-1">
        <CardTitle className="text-lg">
          {row.full_name ?? row.email}
        </CardTitle>
        <p className="text-sm text-slate-300 break-all">{row.email}</p>
        <p className="text-xs text-slate-400">
          Joined {row.created_at}
          {row.last_seen_at ? ` · last seen ${row.last_seen_at}` : " · never seen"}
          {row.invite_status ? ` · invite ${row.invite_status}` : ""}
        </p>
      </div>

      {isSelf ? (
        <p className="mt-4 text-xs text-slate-400">
          You can't change your own role or status. Ask another platform admin.
        </p>
      ) : (
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <Select
            label="Role"
            value={row.role}
            onChange={(e) => void update({ role: e.target.value })}
            disabled={busy}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
          <Select
            label="Organization"
            value={row.organization_id ?? ""}
            onChange={(e) =>
              void update({
                organization_id: e.target.value ? Number(e.target.value) : null,
              })
            }
            disabled={busy}
          >
            <option value="">— None —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
          <div className="flex items-end">
            <Button
              variant={row.is_active ? "danger" : "secondary"}
              disabled={busy}
              onClick={() => void update({ is_active: !row.is_active })}
              className="w-full"
            >
              {row.is_active ? "Suspend account" : "Reinstate account"}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}
    </Card>
  );
}
